import { ReportsTransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import {
  getNPSConfig,
  renderNPSRating,
} from '@/components/shared/surveyHelpers';
import { KImage, KText } from '@/ui-library/components';
import { Flex, Link } from '@chakra-ui/react';

export const CandidateName = (row) => {
  const { ats_application = {} } = row?.original ?? {};
  const applicationData = ats_application?.ats_application ?? ats_application;
  const {
    contact = {},
    is_transferred_in,
    transferred_from,
    is_transferred_out,
    transferred_to,
    transfer_chain,
  } = applicationData ?? {};
  const {
    first_name = '',
    last_name = '',
    id,
    image_url,
    name,
  } = contact ?? {};
  const candidateName = first_name
    ? `${first_name || ' '} ${last_name || ''}`
    : name;

  // Show the transfer history for anyone transferred in (destination or intermediary job); the
  // "transferred to" badge only for a pure origin job — one badge instead of two icons.
  const showTransferredInBadge = is_transferred_in && transferred_from;
  const showTransferredOutBadge =
    is_transferred_out && transferred_to && !is_transferred_in;

  return (
    <>
      {candidateName ? (
        <Flex alignItems="center" gap="6px" w="100%">
          <Link
            href={`/candidates/${id}`}
            target="_blank"
            display="flex"
            alignItems="center"
            gap="6px"
          >
            <KImage
              name={candidateName}
              src={image_url}
              backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
            />
            <KText
              maxW="100%"
              isTruncated
              enableTooltip={true}
              size="sm-regular"
              _hover={{
                color: 'primary-600',
                textDecor: 'underline',
                cursor: 'pointer',
              }}
            >
              {candidateName}
            </KText>
          </Link>
          {showTransferredInBadge && (
            <ReportsTransferredBadge
              type="from"
              jobName={transferred_from.job_name}
              transferredBy={transferred_from.transferred_by}
              transferredAt={transferred_from.transferred_at}
              chain={transfer_chain}
            />
          )}
          {showTransferredOutBadge && (
            <ReportsTransferredBadge
              type="to"
              jobName={transferred_to.job_name}
              transferredBy={transferred_to.transferred_by}
              transferredAt={transferred_to.transferred_at}
            />
          )}
        </Flex>
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </>
  );
};

export const JobName = (row) => {
  const { job_name, job_id } = row?.original ?? {};
  return (
    <>
      {job_name ? (
        <Link href={`/jobs/${job_id}`} target="_blank" w="100%">
          <KText
            maxW="100%"
            isTruncated
            enableTooltip={true}
            size="sm-regular"
            _hover={{
              color: 'primary-600',
              textDecor: 'underline',
              cursor: 'pointer',
            }}
          >
            {job_name}
          </KText>
        </Link>
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </>
  );
};

export const HiringManager = (row) => {
  const { primary_hm = {} } = row?.original ?? {};
  const { name, avatar } = primary_hm ?? {};

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText
            maxW="100%"
            enableTooltip={true}
            isTruncated
            size="sm-regular"
            textTransform="capitalize"
          >
            {name}
          </KText>
        </Flex>
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </>
  );
};

export const Recruiter = (row) => {
  const { primary_recruiter = {} } = row?.original ?? {};
  const { name, avatar } = primary_recruiter ?? {};

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText
            maxW="100%"
            enableTooltip={true}
            isTruncated
            size="sm-regular"
            textTransform="capitalize"
          >
            {name}
          </KText>
        </Flex>
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </>
  );
};

export const EmployeeName = (row) => {
  const { hm_user = {} } = row?.original || {};
  const { name, avatar } = hm_user || {};

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText
            maxW="100%"
            enableTooltip={true}
            isTruncated
            size="sm-regular"
            textTransform="capitalize"
          >
            {name}
          </KText>
        </Flex>
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </>
  );
};

export const Department = (row) => {
  const { department } = row?.original ?? {};
  return <KText size="sm-regular">{department || '-'}</KText>;
};

export const Nps = (row) => {
  const { nps } = row?.original ?? {};
  return <>{renderNPSRating(nps, 10, getNPSConfig(nps, false))}</>;
};

export const SurveyName = (row) => {
  const { survey_name, survey_id } = row?.original ?? {};
  return (
    <>
      {survey_name ? (
        <Link
          href={`/settings/survey/${survey_id}/?tab=submissions`}
          target="_blank"
          w="100%"
        >
          <KText
            size="sm-regular"
            _hover={{
              color: 'primary-600',
              textDecor: 'underline',
              cursor: 'pointer',
            }}
          >
            {survey_name}
          </KText>
        </Link>
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </>
  );
};
